import { Theme, useMediaQuery } from '@material-ui/core';
import { grey, red } from '@material-ui/core/colors';
import { createTheme } from '@material-ui/core/styles';
import shadows from '@material-ui/core/styles/shadows';
import { useMemo } from 'react';

import { typography } from '~/theme/core/typography';

import { useSettings } from '../contexts/SettingsContext';

const useTheme = (): Theme => {
  const { themeMode = 'system' } = useSettings();

  const darkColor = grey[400];

  const preferDark = useMediaQuery('(prefers-color-scheme: dark)');

  const dark = useMemo(() => {
    if (themeMode === 'system') {
      return preferDark;
    } else {
      return themeMode === 'dark';
    }
  }, [preferDark, themeMode]);

  return useMemo(
    () =>
      createTheme({
        shape: {
          borderRadius: 8,
        },
        palette: {
          type: dark ? 'dark' : 'light',
          primary: {
            ...(dark ? { main: '#FF8689' } : { main: red[900] }),
          },
          secondary: {
            main: grey[400],
          },
          background: {
            default: dark ? '#191919' : grey[100],
          },
          ...(dark && { text: { primary: darkColor } }),
          ...(dark && { error: { main: '#FF8689' } }),
        },
        overrides: {
          MuiButton: {
            contained: {
              color: dark ? 'black' : 'white',
              backgroundColor: dark ? grey[400] : grey['800'],
              '&:hover': {
                backgroundColor: dark ? grey[500] : grey[900],
              },
            },
          },
          MuiPaper: {
            root: {
              backgroundColor: dark ? '#202020' : grey[50],
            },
          },
          MuiCard: {
            // Default outlined cards use theme.palette.divider, which is
            // rgba(255, 255, 255, 0.12) in dark mode — nearly invisible
            // against #202020 paper. Bump to ~23% so nested cards (routine
            // day/exercise blocks) read as distinct containers.
            ...(dark && {
              root: {
                '&.MuiPaper-outlined': {
                  borderColor: 'rgba(255, 255, 255, 0.23)',
                },
              },
            }),
          },
          MuiCssBaseline: {
            '@global': {
              body: {
                ...(dark && { color: darkColor }),
              },
            },
          },
        },
        typography,
        shadows,
        props: {
          MuiButton: {
            disableElevation: true,
            variant: 'outlined',
          },
          MuiFilledInput: {
            margin: 'dense',
          },
          MuiFormControl: {
            variant: 'outlined',
            margin: 'dense',
          },
          MuiFormHelperText: {
            margin: 'dense',
          },
          MuiIconButton: {
            size: 'small',
          },
          MuiInputBase: {
            margin: 'dense',
          },
          MuiInputLabel: {
            margin: 'dense',
          },
          MuiListItem: {
            dense: true,
          },
          MuiOutlinedInput: {
            margin: 'dense',
          },
          MuiSelect: {
            variant: 'outlined',
          },
          MuiTable: {
            size: 'small',
          },
          MuiTextField: {
            variant: 'outlined',
            InputLabelProps: { shrink: true },
            margin: 'dense',
          },
          MuiToolbar: {
            variant: 'dense',
          },
        },
      }),
    [dark, darkColor]
  );
};

export default useTheme;
